import {
  authControllerGetAuthDetails,
  GetAuthDetailsResponseDto,
} from "@/lib/gen-api";
import { client as apiClient } from "@/lib/gen-api/client.gen";
import { client as brainClient } from "@/lib/gen-brain/client.gen";
import { captureException } from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { FirebaseOptions, initializeApp } from "firebase/app";
import {
  fetchSignInMethodsForEmail,
  getAuth as firebaseGetAuth,
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  type AuthError,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const TOKEN_REFRESH_INTERVAL = 10000;
// Used for legacy authentication and e2e tests.
const LOCAL_STORAGE_AUTH_TOKEN = "99r-auth-token";
// Used specifically for admin impersonation.
const LOCAL_STORAGE_IMPERSONATION_TOKEN = "99r-impersonation-token";

export const ACCOUNT_NOT_SETUP_MESSAGE =
  "Your account has not been set up yet. Please contact support to get access before signing in.";

const firebaseConfig: FirebaseOptions = {
  apiKey: "AIzaSyBv1XCjOF9rpPRTiW4IuMhhDdDXZBYMl68",
  authDomain: "auth.99ravens.ai",
};

type AuthDetails = GetAuthDetailsResponseDto;

type AuthContextType = {
  token: string | undefined;
  authDetails: AuthDetails | undefined;
  isLoading: boolean;
  isImpersonating: boolean;
};

const AuthContext = createContext<AuthContextType>({
  token: undefined,
  authDetails: undefined,
  isLoading: true,
  isImpersonating: false,
});

const app = initializeApp(firebaseConfig);

type ProviderOption = "google" | "microsoft";

const PROVIDER_LABELS: Record<string, string> = {
  "google.com": "Google",
  "microsoft.com": "Microsoft",
};

const isFirebaseAuthError = (error: unknown): error is AuthError =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  typeof (error as AuthError).code === "string";

const getProviderLabel = (providerId: string): string =>
  PROVIDER_LABELS[providerId] ?? providerId;

const getAccountExistsMessage = async (
  error: AuthError,
  attemptedProvider: ProviderOption,
): Promise<string> => {
  const email = error.customData.email;
  if (typeof email === "string") {
    const methods = await fetchSignInMethodsForEmail(
      firebaseGetAuth(app),
      email,
    );
    const existingProvider = methods[0];
    if (existingProvider) {
      const label = getProviderLabel(existingProvider);
      return `This email is already linked to ${label}. Please sign in with ${label} instead.`;
    }
  }

  const otherProvider = attemptedProvider === "google" ? "Microsoft" : "Google";
  return `This email is already linked to ${otherProvider}. Please sign in with ${otherProvider} instead.`;
};

const getProviderToken = async (provider: ProviderOption): Promise<string> => {
  const authProvider =
    provider === "google"
      ? new GoogleAuthProvider()
      : new OAuthProvider("microsoft.com");

  authProvider.setCustomParameters({
    prompt: "select_account", // This forces user to always select account when logging in
  });

  const auth = firebaseGetAuth(app);
  const credentials = await signInWithPopup(auth, authProvider);

  return credentials.user.getIdToken();
};

export const signIn = async (
  provider: ProviderOption,
): Promise<{ token: string }> => {
  try {
    const firebaseToken = await getProviderToken(provider);

    // Clear any impersonation token
    localStorage.removeItem(LOCAL_STORAGE_IMPERSONATION_TOKEN);

    return { token: firebaseToken };
  } catch (error) {
    if (
      isFirebaseAuthError(error) &&
      (error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled-popup-request")
    ) {
      throw error;
    }

    if (
      isFirebaseAuthError(error) &&
      error.code === "auth/account-exists-with-different-credential"
    ) {
      throw new Error(await getAccountExistsMessage(error, provider));
    }

    captureException(error);
    throw new Error("Sign in failed. Please try again.");
  }
};

export const signOut = async (): Promise<void> => {
  // Sign out from Firebase
  const auth = firebaseGetAuth(app);
  await auth.signOut();

  // Clear tokens from localStorage
  localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
};

export const stopImpersonation = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_IMPERSONATION_TOKEN);
  window.location.reload();
};

/**
 * Use this component to wrap your app.
 */
export const AuthContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [authDetails, setAuthDetails] = useState<AuthDetails | undefined>(
    undefined,
  );
  const contextValue = useMemo(
    () => ({
      token: currentToken ?? undefined,
      authDetails: currentToken ? authDetails : undefined,
      isLoading,
      isImpersonating,
    }),
    [currentToken, authDetails, isLoading, isImpersonating],
  );

  // Listen to Firebase auth state changes
  useEffect(() => {
    const auth = firebaseGetAuth(app);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      const impersonationToken = localStorage.getItem(
        LOCAL_STORAGE_IMPERSONATION_TOKEN,
      );
      const localAuthToken = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);

      if (impersonationToken) {
        setCurrentToken(impersonationToken);
        setIsImpersonating(true);
        setAuthHeadersForClients(impersonationToken);
      } else if (localAuthToken) {
        setCurrentToken(localAuthToken);
        setIsImpersonating(false);
        setAuthHeadersForClients(localAuthToken);
      } else if (user) {
        try {
          // Get fresh Firebase token
          const token = await user.getIdToken();
          setCurrentToken(token);
          setIsImpersonating(false);
          setAuthHeadersForClients(token);
        } catch (error) {
          captureException(error);
          setCurrentToken(null);
          setIsImpersonating(false);
        }
      } else {
        setCurrentToken(null);
        setIsImpersonating(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Auto-refresh Firebase token every minute to keep clients in sync
  useEffect(() => {
    if (!firebaseUser) {
      return; // Don't refresh if not logged in
    }

    const refreshInterval = setInterval(async () => {
      // Don't refresh Firebase token while using a locally stored token
      const hasLocalToken =
        localStorage.getItem(LOCAL_STORAGE_IMPERSONATION_TOKEN) !== null ||
        localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN) !== null;

      if (hasLocalToken) {
        setIsLoading(false);
        return;
      }

      try {
        // Don't force refresh - let Firebase SDK handle expiry logic
        const token = await firebaseUser.getIdToken(false);
        setCurrentToken(token);
        setAuthHeadersForClients(token);
      } catch (error) {
        console.error("Failed to refresh Firebase token:", error);
        captureException(error);
      }

      setIsLoading(false);
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [firebaseUser]);

  // Fetch auth details when we have a token
  useEffect(() => {
    if (!currentToken) {
      setAuthDetails(undefined);
      return;
    }

    if (authDetails) {
      return;
    }

    const fetchAuthDetails = async () => {
      try {
        const response = await authControllerGetAuthDetails();

        if (response.data === undefined) {
          if (response.response.status === 401) {
            toast.error(ACCOUNT_NOT_SETUP_MESSAGE);
            localStorage.removeItem(LOCAL_STORAGE_IMPERSONATION_TOKEN);
            localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
            await signOut();
            navigate({ to: "/u/login" });
            return;
          }

          throw new Error("Failed to fetch auth details: " + response.error);
        }

        setAuthDetails(response.data);
      } catch (error) {
        localStorage.removeItem(LOCAL_STORAGE_IMPERSONATION_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
        await signOut();
        navigate({ to: "/u/login" });
        captureException(error);
      }
    };

    fetchAuthDetails();
  }, [currentToken, authDetails]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};

/**
 * Ensures to return an auth details object even if the auth context is not initialized.
 * This is useful for components that need to display the auth details but don't
 * want to throw an error if the auth context is not initialized. If you need to
 * check if the auth context is initialized, use `useAuthContext`.
 */
export const useAuthDetails = (): AuthDetails | undefined => {
  const context = useContext(AuthContext);

  return context.authDetails;
};

/**
 * Sets the auth headers for all clients. We use different services with the same
 * token, so we need to set the headers for all of them.
 */
function setAuthHeadersForClients(token: string) {
  apiClient.setConfig({
    ...apiClient.getConfig(),
    headers: {
      ...apiClient.getConfig().headers,
      Authorization: `Bearer ${token}`,
    },
  });

  brainClient.setConfig({
    ...brainClient.getConfig(),
    headers: {
      ...brainClient.getConfig().headers,
      Authorization: `Bearer ${token}`,
    },
  });
}
